import React, {useEffect, useState} from 'react'
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, EyeOutlined, CalculatorOutlined, DownloadOutlined } from '@ant-design/icons';
import {Form, Button, message, Row, Col, Card, Statistic, Table, Collapse, Tag, Select, InputNumber, Typography, Space} from 'antd';
import {getVariable, actualizarVariable} from '../../../../apis/CalculoVariableApi';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {getEmpleados, getUMA} from '../../../../apis/CalculoIntegradoApi';

const { Text } = Typography;

const { Panel } = Collapse;
const { Option } = Select;

const routes = [
  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Cálculo Variable</span>
      </>
    ),
  }
];

dayjs.locale('es');

const CalculoVariablePage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingCalculo, setLoadingCalculo] = useState(false);
  const [disabledForm, setDisabled] = useState(true);
  const [resultadoData, setResultadoData] = useState(null);
  const [empleadosData, setEmpleadosData] = useState([]);
  const [empleadosAntesDeAplicar, setEmpleadosAntesDeAplicar] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [uma, setUma] = useState(0);

  useEffect(() => {
    const loadUMAValue = async () => {
      try {
        const res = await getUMA();
        if (res?.ok && res.payload?.monto != null) {
          const montoNum = Number(res.payload.monto);
          if (!Number.isNaN(montoNum)) {
            setUma(montoNum);
          }
        }
      } catch (err) {
        console.error('Error obteniendo el valor de UMA:', err);
      }
    };
    loadUMAValue();
  }, []);

  // Solo meses impares: 1, 3, 5, 7, 9, 11
  const meses = [
    { value: 1, label: 'Enero' },
    { value: 3, label: 'Marzo' },
    { value: 5, label: 'Mayo' },
    { value: 7, label: 'Julio' },
    { value: 9, label: 'Septiembre' },
    { value: 11, label: 'Noviembre' },
  ];

  const handleFieldChange = () => {
    const values = form.getFieldsValue();
    if (values.anio && values.mes && values.dia) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  const handleCalcularIntegrado = async () => {
    const values = form.getFieldsValue();
    if (!values.anio || !values.mes || !values.dia) {
      message.warning('Por favor completa el año, mes y día para calcular');
      return;
    }

    // Guardar el estado actual de los empleados antes de aplicar cambios
    if (empleadosData.length > 0) {
      setEmpleadosAntesDeAplicar([...empleadosData]);
    }

    setLoadingCalculo(true);
    
    try {
      // Crear fecha con los valores proporcionados
      const fechaFormateada = dayjs(`${values.anio}-${values.mes}-${values.dia}`).format('YYYY-MM-DD');
      const response = await actualizarVariable(fechaFormateada);

      if (response && response.ok && response.payload) {
        message.success(`Cálculo integrado completado para ${response.payload.cantidad} empleados!`);
        // Recargar datos después de aplicar
        await handleCalcular();
      } else {
        message.error(response?.message || 'Error al calcular el integrado');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'Error al calcular el integrado');
    } finally {
      setLoadingCalculo(false);
    }
  };

  const handleCalcular = async () => {
    const values = form.getFieldsValue();
    if (!values.anio || !values.mes || !values.dia) {
      message.warning('Por favor completa el año, mes y día');
      return;
    }

    setLoading(true);
    
    try {
      // Crear fecha con los valores proporcionados
      const fechaFormateada = dayjs(`${values.anio}-${values.mes}-${values.dia}`).format('YYYY-MM-DD');
      const response = await getVariable(fechaFormateada);

      if (response && response.ok && response.payload) {
        setResultadoData(response.payload);
        
        // Obtener IDs de empleados del resultado
        const empleadoIds = Object.values(response.payload).map(emp => emp.empleado_id).filter(Boolean);
        
        if (empleadoIds.length > 0) {
          // Obtener datos completos de empleados
          const empleadosResponse = await getEmpleados(empleadoIds);
          if (empleadosResponse?.ok && Array.isArray(empleadosResponse.payload)) {
            setEmpleadosData(empleadosResponse.payload);
          }
        }
        
        message.success('Cálculo realizado correctamente!');
      } else {
        message.error(response?.message || 'Error al calcular variables');
      }
    } catch (error) {
      message.error('Error al calcular las variables');
    } finally {
      setLoading(false);
    }
  };

  const resetBody = async () => {
    form.resetFields();
    setResultadoData(null);
    setEmpleadosData([]);
    setEmpleadosAntesDeAplicar([]);
    setDisabled(true);
    setPageSize(10);
  };

  const handleTableChange = (pagination) => {
    setPageSize(pagination.pageSize);
  };

  const formatTwoDecimals = (value) => {
    const num = Number(value || 0);
    return Number.isFinite(num)
      ? num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';
  };

  const empleadosColumns = [
    {
      title: 'Empleado',
      dataIndex: 'nombre_empleado',
      key: 'nombre_empleado',
      ellipsis: true,
      width: 200,
      sorter: (a, b) => a.nombre_empleado.localeCompare(b.nombre_empleado),
    },
    {
      title: () => (
        <Space direction="vertical" size={0}>
          <Text strong>Valor del UMA</Text>
          <Text type="secondary">${formatTwoDecimals(uma)}</Text>
        </Space>
      ),
      children: [
        {
          title: 'Integrado Anterior',
          dataIndex: 'salario_integrado_anterior',
          key: 'salario_integrado_anterior',
          width: 150,
          render: (value) => `$ ${formatTwoDecimals(value)}`,
          align: 'center',
          sorter: (a, b) => (a.salario_integrado_anterior || 0) - (b.salario_integrado_anterior || 0),
        },
        {
          title: 'Integrado Nuevo',
          dataIndex: 'salario_integrado_nuevo',
          key: 'salario_integrado_nuevo',
          width: 170,
          align: 'center',
          render: (v) => (
            <div
              style={{
                background: '#FFF7E6',
                border: '1px solid #FFE7BA',
                padding: 4,
                borderRadius: 6,
                display: 'inline-block',
                minWidth: 120,
                textAlign: 'center',
              }}
            >
              <Text strong>$ {formatTwoDecimals(v)}</Text>
            </div>
          ),
          sorter: (a, b) => (a.salario_integrado_nuevo || 0) - (b.salario_integrado_nuevo || 0),
        },
      ],
    },
    {
      title: 'Salario Diario',
      dataIndex: 'salario_diario',
      key: 'salario_diario',
      width: 130,
      render: (value) => `$ ${formatTwoDecimals(value)}`,
      align: 'right',
      sorter: (a, b) => (a.salario_diario || 0) - (b.salario_diario || 0),
    },
    {
      title: 'Variable',
      dataIndex: 'promedio_diario',
      key: 'promedio_diario',
      width: 130,
      render: (value) => `$ ${formatTwoDecimals(value)}`,
      align: 'right',
      sorter: (a, b) => a.promedio_diario - b.promedio_diario,
    },
    {
      title: 'Fijo',
      dataIndex: 'fijo',
      key: 'fijo',
      width: 130,
      render: (value) => `$ ${formatTwoDecimals(value)}`,
      align: 'right',
      sorter: (a, b) => (a.fijo || 0) - (b.fijo || 0),
    },
    {
      title: 'Factor',
      dataIndex: 'factor_integracion',
      key: 'factor_integracion',
      width: 90,
      align: 'center',
      sorter: (a, b) => (a.factor_integracion || 0) - (b.factor_integracion || 0),
    },
    {
      title: 'Total Bim.',
      dataIndex: 'total_bimestre',
      key: 'total_bimestre',
      width: 140,
      render: (value) => `$ ${formatTwoDecimals(value)}`,
      align: 'right',
      sorter: (a, b) => a.total_bimestre - b.total_bimestre,
    },
    {
      title: 'Días Bim.',
      dataIndex: 'dias_bimestre',
      key: 'dias_bimestre',
      width: 100,
      align: 'center',
    },
  ];

  const conceptosColumns = [
    {
      title: 'Concepto',
      dataIndex: 'displayTitle',
      key: 'displayTitle',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (value) => `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      align: 'right',
    },
  ];

  const detallesColumns = [
    {
      title: 'Período',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 100,
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (value) => `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      align: 'right',
    },
  ];

  const prepareTableData = () => {
    if (!resultadoData) return [];
    
    return Object.entries(resultadoData).map(([nombreEmpleado, data]) => {
      // Buscar datos del empleado
      const empleadoData = empleadosData.find(emp => emp.id === data.empleado_id);
      const empleadoAnterior = empleadosAntesDeAplicar.find(emp => emp.id === data.empleado_id);
      
      return {
        key: nombreEmpleado,
        empleado_id: data.empleado_id,
        nombre_empleado: nombreEmpleado,
        total_bimestre: data.total_bimestre,
        dias_bimestre: data.dias_bimestre,
        promedio_diario: data.promedio_diario,
        conceptos: data.conceptos || [],
        periodos_incluidos: data.periodos_incluidos || [],
        // Datos del empleado desde Odoo
        salario_diario: empleadoData?.nomina_daily_salary || 0,
        fijo: empleadoData?.nomina_fixed_amount || 0,
        factor_integracion: empleadoData?.nomina_integration_factor || 0,
        // Integrado anterior: si hay estado guardado, usarlo; si no, usar el actual
        salario_integrado_anterior: empleadoAnterior?.nomina_integrated_salary || empleadoData?.nomina_integrated_salary || 0,
        // Integrado nuevo: el actual después de aplicar
        salario_integrado_nuevo: empleadoData?.nomina_integrated_salary || 0,
      };
    });
  };

  const calcularTotales = () => {
    if (!resultadoData) return { totalGeneral: 0, totalEmpleados: 0 };
    
    const datos = Object.values(resultadoData);
    const totalGeneral = datos.reduce((sum, emp) => sum + emp.total_bimestre, 0);
    const totalEmpleados = datos.length;
    
    return { totalGeneral, totalEmpleados };
  };

  const totales = calcularTotales();

  const exportToExcel = () => {
    if (!resultadoData || Object.keys(resultadoData).length === 0) {
      message.warning('No hay datos para exportar.');
      return;
    }

    const data = prepareTableData();

    let htmlTable = `
      <table border="1" cellpadding="5" cellspacing="0">
        <thead>
          <tr style="background-color: #f0f0f0; font-weight: bold;">
            <th>Empleado</th>
            <th>Integrado Anterior</th>
            <th>Integrado Nuevo</th>
            <th>Salario Diario</th>
            <th>Variable</th>
            <th>Fijo</th>
            <th>Factor Integración</th>
            <th>Total Bimestre</th>
            <th>Días Bimestre</th>
          </tr>
        </thead>
        <tbody>
    `;

    data.forEach((row) => {
      htmlTable += `
        <tr>
          <td>${row.nombre_empleado}</td>
          <td>$ ${formatTwoDecimals(row.salario_integrado_anterior)}</td>
          <td>$ ${formatTwoDecimals(row.salario_integrado_nuevo)}</td>
          <td>$ ${formatTwoDecimals(row.salario_diario)}</td>
          <td>$ ${formatTwoDecimals(row.promedio_diario)}</td>
          <td>$ ${formatTwoDecimals(row.fijo)}</td>
          <td>${row.factor_integracion}</td>
          <td>$ ${formatTwoDecimals(row.total_bimestre)}</td>
          <td>${row.dias_bimestre}</td>
        </tr>
      `;
    });

    htmlTable += `
        </tbody>
      </table>
    `;

    const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const values = form.getFieldsValue();
    link.download = `Calculo_Variable_${values.mes}_${values.anio}_${dayjs().format('YYYY-MM-DD')}.xls`;
    link.click();
    URL.revokeObjectURL(url);

    message.success('Archivo exportado correctamente.');
  };

  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card size="small" title="Períodos Incluidos" style={{ marginBottom: 16 }}>
              {record.periodos_incluidos.map((periodo, idx) => (
                <Tag key={idx} color="blue">
                  Período {periodo.num_periodo}/{periodo.anio} - {periodo.dias} días
                </Tag>
              ))}
            </Card>
          </Col>

          <Col span={24}>
            <Card size="small" title="Conceptos">
              <Collapse accordion>
                {record.conceptos.map((concepto, idx) => (
                  <Panel 
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><strong>{concepto.displayTitle}</strong></span>
                        <span style={{ color: '#1890ff' }}>
                          ${concepto.total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    } 
                    key={idx}
                  >
                    <Table
                      size="small"
                      dataSource={concepto.detalles}
                      columns={detallesColumns}
                      pagination={false}
                      rowKey={(record, index) => `${concepto.clave}-${index}`}
                    />
                  </Panel>
                ))}
              </Collapse>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <PageApp routes={routes} title={'Cálculo Variable'}>
      <Row gutter={16}>
        <Col span={24}>
          <Card title="Cálculo Variable" style={{ marginBottom: 16 }}>
            <Form layout="vertical" form={form} autoComplete="off">
              <Row gutter={16}>
                <Col md={8} sm={8} xs={24}>
                  <Form.Item
                    name="anio"
                    label="Año"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor ingresa el año',
                      },
                    ]}
                  >
                    <InputNumber 
                      placeholder="Año" 
                      style={{ width: '100%' }}
                      min={2000}
                      max={2100}
                      onChange={handleFieldChange}
                    />
                  </Form.Item>
                </Col>
                <Col md={8} sm={8} xs={24}>
                  <Form.Item
                    name="mes"
                    label="Mes"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor selecciona el mes',
                      },
                    ]}
                  >
                    <Select 
                      placeholder="Selecciona un mes"
                      onChange={handleFieldChange}
                    >
                      {meses.map((mes) => (
                        <Option key={mes.value} value={mes.value}>
                          {mes.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={8} sm={8} xs={24}>
                  <Form.Item
                    name="dia"
                    label="Día"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor ingresa el día',
                      },
                    ]}
                  >
                    <InputNumber 
                      placeholder="Día" 
                      style={{ width: '100%' }}
                      min={1}
                      max={31}
                      onChange={handleFieldChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Button
                    type="default"
                    icon={<EyeOutlined />}
                    onClick={handleCalcular}
                    disabled={disabledForm}
                    loading={loading}
                    style={{ marginRight: 8 }}
                  >
                    Visualizar
                  </Button>
                  <Button
                    type="primary"
                    icon={<CalculatorOutlined />}
                    onClick={handleCalcularIntegrado}
                    disabled={disabledForm}
                    loading={loadingCalculo}
                    style={{ marginRight: 8 }}
                  >
                    Calcular Integrado
                  </Button>
                  <Button onClick={resetBody}>
                    Limpiar
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      {resultadoData && (
        <Row gutter={16}>
          <Col span={12}>
            <Card style={{ marginBottom: 16 }}>
              <Statistic
                title="Total Empleados"
                value={totales.totalEmpleados}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card 
              title="Resultados por Empleado"
              extra={
                <Button
                  icon={<DownloadOutlined />}
                  onClick={exportToExcel}
                  disabled={!resultadoData || Object.keys(resultadoData).length === 0}
                >
                  Exportar a Excel
                </Button>
              }
            >
              <Table
                dataSource={prepareTableData()}
                columns={empleadosColumns}
                scroll={{ x: 'max-content' }}
                expandable={{
                  expandedRowRender,
                  expandRowByClick: true,
                }}
                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} empleados`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
                loading={loading}
              />
            </Card>
          </Col>
        </Row>
      )}
    </PageApp>
  );
}

export default CalculoVariablePage;
